import {
  clearRefreshToken,
  getRefreshToken,
  setRefreshToken,
} from "./storage";

// 빌드 시점에 박힘. 비어 있으면 같은 origin 으로 (dev 모드에서 vite proxy 가 처리).
// import.meta 타입은 UI 패키지 자체엔 Vite 가 없어서 직접 가드.
const API_BASE = (
  (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? ""
).replace(/\/+$/, "");

function resolve(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path}`;
}

const ACCESS_KEY = "toss_ads.access_token";

// 같은 탭의 reload 만 살아남는 sessionStorage 사용 — XSS 노출은 메모리와 큰 차이 없고
// reload 마다 refresh 호출 안 해도 됨.
function readAccess(): string | null {
  try {
    return sessionStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}
function writeAccess(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(ACCESS_KEY, token);
    else sessionStorage.removeItem(ACCESS_KEY);
  } catch {
    /* 무시 */
  }
}

let accessToken: string | null = readAccess();
let refreshPromise: Promise<void> | null = null;
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  writeAccess(token);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onUnauthorized(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ApiInit = Omit<RequestInit, "body"> & { body?: unknown };

export async function apiClient<T = unknown>(
  path: string,
  init: ApiInit = {},
): Promise<T> {
  return apiCall<T>(path, init, true);
}

async function apiCall<T>(
  path: string,
  init: ApiInit,
  allowRefresh: boolean,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    if (
      init.body instanceof FormData ||
      init.body instanceof Blob ||
      typeof init.body === "string"
    ) {
      body = init.body;
    } else {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(init.body);
    }
  }

  const res = await fetch(resolve(path), { ...init, headers, body });

  if (res.status === 401 && allowRefresh) {
    try {
      await ensureRefresh();
      return apiCall<T>(path, init, false);
    } catch {
      onSessionExpired?.();
      throw new ApiError(401, null, "Session expired");
    }
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  const parsed = contentType.includes("json")
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    const message =
      parsed && typeof parsed === "object" && "message" in parsed
        ? String((parsed as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, message);
  }

  return parsed as T;
}

// 외부에서도 사용 — 페이지 부팅 시 refresh 를 명시적으로 먼저 해서
// access token 확보 후 /me 호출하면 401 → refresh → retry 사이클의
// 첫 401 이 안 떨어지고 Network 탭 깨끗해짐.
export function refreshSession(): Promise<void> {
  return ensureRefresh();
}

async function ensureRefresh(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("no refresh token");

    const res = await fetch(resolve("/api/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      setAccessToken(null);
      clearRefreshToken();
      throw new Error(`refresh failed: ${res.status}`);
    }

    const data = (await res.json()) as {
      accessToken: string;
      refreshToken: string;
    };
    setAccessToken(data.accessToken);
    setRefreshToken(data.refreshToken);
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

export function clearTokens(): void {
  setAccessToken(null);
  clearRefreshToken();
}

export async function apiClientBlob(
  path: string,
  init: Omit<RequestInit, "body"> = {},
): Promise<{ blob: Blob; headers: Headers }> {
  return blobCall(path, init, true);
}

async function blobCall(
  path: string,
  init: Omit<RequestInit, "body">,
  allowRefresh: boolean,
): Promise<{ blob: Blob; headers: Headers }> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  const res = await fetch(resolve(path), { ...init, headers });

  if (res.status === 401 && allowRefresh) {
    try {
      await ensureRefresh();
      return blobCall(path, init, false);
    } catch {
      onSessionExpired?.();
      throw new ApiError(401, null, "Session expired");
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, null, `HTTP ${res.status}`);
  }

  const blob = await res.blob();
  return { blob, headers: res.headers };
}
